import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useTheme,
  Chip,
  Card,
  TextField,
  Divider,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  KeyboardArrowDownOutlined as ExpandMoreIcon,
  Apps as AppsIcon,
  EmailOutlined as EmailOutlinedIcon,
  LanguageOutlined as LanguageOutlinedIcon,
  AccountTreeOutlined as AccountTreeOutlinedIcon,
  TranslateOutlined as TranslateOutlinedIcon,
  DataObjectOutlined as DataObjectOutlinedIcon,
} from '@mui/icons-material';
import { PanelLeftClose, PanelRightOpen } from 'lucide-react';
import { Variant } from 'types/cx-editor';
import { SECTION_ID_TO_ELEMENT_KEY_MAP } from 'utils/html-section-parser';
import { isHerTemplate } from 'utils/her-preview-url';
import { normalizeFuelType, getFuelTypeLabel, buildFuelTypeTemplateSet } from 'utils/templates-from-feature-metrics';
import type { GetFeatureMetric } from 'types/apis/clusterInterfaces';

// FeatureResolution type from GetFeatureMetric namespace
type FeatureResolution = GetFeatureMetric.retval[number];

// Template types
interface ImpactTemplate {
  productType: string;    // e.g., "EMAIL.SF.MONTHLY_SUMMARY"
  product: string;        // e.g., "EMAIL"
  fuelType: string;       // e.g., "ELECTRIC"
  sections: string[];
}

interface TemplateComponent {
  id: string;
  name: string;
  selected?: boolean;
}

interface Template {
  /** Unique per row (productType + fuel when impact lists multiple fuels for same productType). */
  id: string;
  /** API template key (productType). Omitted for synthetic WEB entries where id is the key. */
  productType?: string;
  fuelType?: string;
  name: string;
  icon?: string;
  components: TemplateComponent[];
  expanded?: boolean;
}

interface Channel {
  id: string;
  name: string;
  templates: Template[];
  expanded?: boolean;
}

// Main component props
interface EditColorSidebarProps {
  // Color Details
  hexValue: string;
  onHexChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onHexBlur: () => void;

  // Used In
  templates?: ImpactTemplate[];
  level1Data?: FeatureResolution[];  // Level 1 API data to determine fuel-type vs no-fuel templates
  level1Loading?: boolean;            // Loading state for Level 1 data
  level1Error?: string | null;        // Error state for Level 1 data
  loading?: boolean;
  onComponentSelect?: (templateKey: string, componentName: string, templateFuelType?: string) => void;
  selectedComponent?: { templateKey: string; componentName: string; fuelType?: string } | null;
  
  variant: Variant;
  onVariantChange: (variant: Partial<Variant>) => void;
  availableLocales: string[];
  availableScenarios: Array<{ value: string; label: string }>;
  scenariosLoading?: boolean;
  
  // Sidebar state
  isOpen: boolean;
  onToggleOpen: () => void;
  
  // Read-only mode (for Bidgely Standard colors)
  isReadOnly?: boolean;
  
  isSdkReady?: boolean;
  
  /** Channel from Color Palette page (digital/paper). When PAPER, Web Widgets are hidden. */
  channel?: 'DIGITAL' | 'PAPER';

  /** CONTENT clusters from GET /v1/get-clusters/<projectId>?cluster_categories=CONTENT (reference = label, name = tooltip, id = cluster_id for API) */
  availableClusters?: Array<{ reference: string; name: string; id: string }>;
  clustersLoading?: boolean;
  /** When set, empty state "No content cluster is present." is shown only after fetch completed with no clusters. */
  projectId?: string;
  /** Called when the Hierarchy (cluster) dropdown is opened; parent should refetch clusters. */
  onClustersDropdownOpen?: () => void;
}

/** Impact can list the same productType twice (e.g. EL + GAS Bill Projection); match selection including fuel. */
function impactFuelsMatch(
  selectedFuel: string | undefined,
  templateFuel: string | undefined
): boolean {
  if (templateFuel == null || String(templateFuel).trim() === '') {
    return selectedFuel == null || String(selectedFuel).trim() === '';
  }
  return normalizeFuelType(selectedFuel) === normalizeFuelType(templateFuel);
}

// Helper to map section ID to user-friendly display name
const getSectionDisplayName = (sectionId: string): string => {
  // Try exact match first
  let displayName = SECTION_ID_TO_ELEMENT_KEY_MAP[sectionId];
  
  // If no exact match, try removing version suffix (e.g., _V2, _V3, _V4)
  if (!displayName) {
    const baseId = sectionId.replace(/_[Vv]\d+$/, '');
    displayName = SECTION_ID_TO_ELEMENT_KEY_MAP[baseId];
  }
  
  // If still no match, try removing all version suffixes
  if (!displayName) {
    const cleanId = sectionId.replace(/_[Vv]\d+.*$/, '');
    displayName = SECTION_ID_TO_ELEMENT_KEY_MAP[cleanId];
  }
  
  // Fallback: convert SNAKE_CASE to Title Case
  if (!displayName) {
    displayName = sectionId
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }
  
  return displayName;
};

const getWebWidgetDisplayName = (templateKey: string): string => {
  const parts = templateKey.split('.');
  const lastPart = parts[parts.length - 1] || templateKey;

  return lastPart
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

export const EditColorSidebar: React.FC<EditColorSidebarProps> = ({
  hexValue,
  onHexChange,
  onHexBlur,
  templates = [],
  level1Data = [],
  level1Loading = false,
  level1Error = null,
  loading = false,
  onComponentSelect,
  selectedComponent,
  variant,
  onVariantChange,
  availableLocales = [],
  availableScenarios = [],
  scenariosLoading = false,
  isOpen,
  onToggleOpen,
  isReadOnly = false,
  isSdkReady = false,
  channel = 'DIGITAL',
  availableClusters = [] as Array<{ reference: string; name: string; id: string }>,
  clustersLoading = false,
  projectId,
  onClustersDropdownOpen,
}) => {
  const theme = useTheme();
  const [expandedChannels, setExpandedChannels] = useState<string[]>(['EMAIL']);
  const [expandedTemplates, setExpandedTemplates] = useState<string[]>([]);
  const colorPickerRef = useRef<HTMLInputElement>(null);

  // Auto-collapse WEB accordion when SDK is not ready
  useEffect(() => {
    if (!isSdkReady && expandedChannels.includes('WEB')) {
      setExpandedChannels((prev) => prev.filter((id) => id !== 'WEB'));
    }
  }, [isSdkReady, expandedChannels]);

  // Auto-expand HER channel and selected HER template when navigating from InspectorPanel with HER template
  useEffect(() => {
    const key = selectedComponent?.templateKey;
    if (key && isHerTemplate(key)) {
      setExpandedChannels((prev) => (prev.includes('HER') ? prev : [...prev, 'HER']));
      setExpandedTemplates((prev) => (prev.includes(key) ? prev : [...prev, key]));
    }
  }, [selectedComponent?.templateKey]);

  useEffect(() => {
    console.log('[EditColorSidebar] isSdkReady prop:', isSdkReady);
  }, [isSdkReady]);

  const handleColorSwatchClick = () => {
    if (isReadOnly) return;
    colorPickerRef.current?.click();
  };

  const handleColorPickerChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isReadOnly) return;
    const syntheticEvent = {
      target: {
        value: event.target.value.toUpperCase(),
      },
    } as React.ChangeEvent<HTMLInputElement>;
    onHexChange(syntheticEvent);
  };

  // Build set of template base keys (without fuel suffix) that have fuel variants in Level 1
  // This determines which templates are fuel-specific (same logic as Template Editor)
  // Uses shared utility function for consistency across codebase
  const fuelTypeTemplateBaseKeys = React.useMemo(() => {
    // TypeScript validation: ensure level1Data is always FeatureResolution[]
    if (!Array.isArray(level1Data)) {
      console.warn('[EditColorSidebar] level1Data is not an array:', level1Data);
      return new Set<string>();
    }

    return buildFuelTypeTemplateSet(level1Data);
  }, [level1Data]);

  // Group templates by channel (product) and transform to component structure
  const channels: Channel[] = React.useMemo(() => {
    const channelMap = new Map<string, Template[]>();

    // Helper to check if template is a fuel-type template (has fuel variants in Level 1)
    const isFuelTypeTemplate = (productType: string): boolean => {
      return fuelTypeTemplateBaseKeys.has(productType);
    };

    // Helper to check if fuelType is valid (not event names)
    const isValidFuelType = (fuelType: string | undefined): boolean => {
      if (!fuelType) return false;
      const normalized = fuelType.toUpperCase().trim();
      return ['ELECTRIC', 'GAS', 'WATER', 'DUAL', 'DUAL FUEL'].includes(normalized);
    };

    // Helper to get display label for no-fuel templates (e.g., "DF" from "EMAIL.DF.USER_WELCOME")
    const getNoFuelDisplayLabel = (templateKey: string): string => {
      const parts = templateKey.split('.');
      if (parts.length >= 2) {
        return parts[1].toUpperCase();
      }
      return '';
    };

    // Track which templates we've already added
    const addedTemplates = new Set<string>();

    templates.forEach((template) => {
      const channelName = template.product;

      if (!channelMap.has(channelName)) {
        channelMap.set(channelName, []);
      }

      // Derive display name from productType
      const templateKey = template.productType;

      // Check if this template has fuel variants in Level 1 (fuel-type template)
      const hasFuelVariants = isFuelTypeTemplate(templateKey);

      // Create unique key for deduplication
      const uniqueKey = hasFuelVariants
        ? `${templateKey}|${template.fuelType}` // Keep each fuel variant separate for fuel-type templates
        : templateKey;                           // Deduplicate no-fuel templates by productType only

      // Skip if already added
      if (addedTemplates.has(uniqueKey)) {
        return;
      }
      addedTemplates.add(uniqueKey);

      const parts = templateKey.split('.');
      const segmentForDisplay = channelName === 'HER' && parts.length > 1
        ? parts[1]
        : (parts[parts.length - 1] || templateKey);
      const displayName = segmentForDisplay
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');

      // Determine icon to display:
      // - Fuel-type templates (has fuel variants in Level 1): Show fuel type chip (GAS, EL, WATER)
      // - No-fuel templates (no fuel variants in Level 1): Show pilot type chip (DF, SF)
      let icon: string;

      if (hasFuelVariants && isValidFuelType(template.fuelType)) {
        // Fuel-type template: Use fuel type from backend
        // e.g., template.fuelType "GAS" → "GAS", "ELECTRIC" → "EL"
        icon = getFuelTypeLabel(template.fuelType);
      } else {
        // No-fuel template: Extract display label from template key
        // e.g., "EMAIL.DF.USER_WELCOME" → "DF"
        icon = getNoFuelDisplayLabel(templateKey) || 'DF';
      }

      const instanceId = `${templateKey}|${(template.fuelType && String(template.fuelType).trim()) || 'DEFAULT'}`;

      const components: TemplateComponent[] = template.sections.map((section) => ({
        id: section,
        name: getSectionDisplayName(section),
        selected:
          selectedComponent?.templateKey === templateKey &&
          selectedComponent?.componentName === section &&
          impactFuelsMatch(selectedComponent?.fuelType, template.fuelType),
      }));

      channelMap.get(channelName)!.push({
        id: instanceId,
        productType: templateKey,
        fuelType: template.fuelType,
        name: displayName,
        icon: icon,
        components: components,
        expanded: expandedTemplates.includes(instanceId),
      });
    });


    // Only add Web Widgets when channel is DIGITAL (paper channel has no web widgets)
    if (channel === 'DIGITAL') {
      const webWidgetTemplateKeys = [
        'VIEW_ALL_WEB_WIDGETS',
        'DASHBOARD.HOME.BILL_PROJECTION',
        'DASHBOARD.HOME.BILL_SUMMARY',
        'DASHBOARD.HOME.BILL_ITEMIZATION',
        'DASHBOARD.ENERGY_INSIGHTS.APPLIANCE',
        'DASHBOARD.ENERGY_INSIGHTS.BILL_ANALYSIS',
      ];

      if (!channelMap.has('WEB')) {
        channelMap.set('WEB', []);
      }

      webWidgetTemplateKeys.forEach((templateKey) => {
        const displayName = templateKey === 'VIEW_ALL_WEB_WIDGETS'
          ? 'View All Web Widgets'
          : getWebWidgetDisplayName(templateKey);

        channelMap.get('WEB')!.push({
          id: templateKey,
          name: displayName,
          components: [],
          expanded: expandedTemplates.includes(templateKey),
        });
      });
    }

    return Array.from(channelMap.entries()).map(([channelName, channelTemplates]) => ({
      id: channelName,
      name: channelName,
      templates: channelTemplates,
      expanded: expandedChannels.includes(channelName),
    }));
  }, [templates, expandedTemplates, selectedComponent, expandedChannels, channel, fuelTypeTemplateBaseKeys]);

  const handleChannelToggle = (channelId: string) => {
    setExpandedChannels((prev) =>
      prev.includes(channelId)
        ? prev.filter((id) => id !== channelId)
        : [...prev, channelId]
    );
  };

  const handleTemplateToggle = (templateKey: string) => {
    setExpandedTemplates((prev) =>
      prev.includes(templateKey)
        ? prev.filter((key) => key !== templateKey)
        : [...prev, templateKey]
    );
  };

  const handleComponentClick = (templateKey: string, componentName: string, templateFuelType?: string) => {
    if (onComponentSelect) {
      onComponentSelect(templateKey, componentName, templateFuelType);
    }
  };

  const getChannelIcon = (channelName: string) => {
    const channelUpper = channelName.toUpperCase();
    if (channelUpper.includes('EMAIL') || channelUpper === 'EMAIL') {
      return <EmailOutlinedIcon sx={{ fontSize: 16 }} />;
    } else if (channelUpper.includes('WEB') || channelUpper.includes('WIDGET')) {
      return <LanguageOutlinedIcon sx={{ fontSize: 16 }} />;
    }
    return null;
  };

  // Render template usage accordion
  const renderTemplateUsage = () => (
    <Box sx={{ height: '100%', overflow: 'auto' }}>
      {channels.map((channel) => {
        const templateCount = channel.templates.length;
        const isWebChannel = channel.name === 'WEB';
        const isDisabled = isWebChannel && !isSdkReady;
        
        return (
          <Accordion
            key={channel.id}
            expanded={expandedChannels.includes(channel.id)}
            onChange={() => handleChannelToggle(channel.id)}
            disableGutters
            elevation={0}
            disabled={isDisabled}
            sx={{
              '&:before': { display: 'none' },
              '&.Mui-expanded': { margin: 0 },
              mb: 0.5,
              border: theme.palette.mode === 'dark' ? '1px solid' : 'none',
              borderColor: theme.palette.mode === 'dark' ? theme.palette.divider : 'transparent',
              borderRadius: 1,
              opacity: isDisabled ? 0.8 : 1,
            }}
          >
            <Tooltip
              title={isDisabled ? 'Widget SDK is initializing. Web widgets will be available once the SDK is ready.' : ''}
              placement="right"
              arrow
            >
              <AccordionSummary
                expandIcon={
                  <ExpandMoreIcon 
                    sx={{ 
                      fontSize: 16,
                      color: theme.palette.mode === 'dark' ? theme.palette.common.white : 'inherit',
                    }} 
                  />
                }
                sx={{
                  minHeight: 40,
                  '&.Mui-expanded': { minHeight: 40 },
                  px: 1.5,
                  py: 0.5,
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    color: isDisabled ? 'text.disabled' : theme.palette.text.secondary,
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    textAlign: 'left',
                  }}
                >
                  {getChannelIcon(channel.name)}
                  {channel.name === 'WEB' 
                    ? `${channel.name} Widgets`
                    : `${channel.name} Templates (${templateCount})`}
                </Typography>
              </AccordionSummary>
            </Tooltip>
            <AccordionDetails sx={{ pt: 0.5, pb: 0.5, px: 1 }}>
              {channel.name === 'WEB' ? (
                <List dense disablePadding>
                  {channel.templates.map((template) => (
                    <Tooltip key={template.id} title={template.id} placement="right">
                      <ListItem
                        disablePadding
                        sx={{ mb: 0.5 }}
                      >
                        <ListItemButton
                          onClick={() => handleComponentClick(template.id, template.id)}
                          selected={
                            selectedComponent?.templateKey === template.id ||
                            (template.id === 'VIEW_ALL_WEB_WIDGETS' && selectedComponent?.templateKey === 'DASHBOARD')
                          }
                          sx={{
                            borderRadius: 1,
                            py: 0.5,
                            px: 1.5,
                            minHeight: 32,
                            '&.Mui-selected': {
                              bgcolor: theme.palette.mode === 'dark' ? '#008080' : theme.palette.primary.main,
                              color: theme.palette.primary.contrastText,
                              '&:hover': {
                                bgcolor: theme.palette.mode === 'dark' ? '#008080' : theme.palette.primary.main,
                              },
                              '& .MuiListItemIcon-root': {
                                color: theme.palette.primary.contrastText,
                              },
                              '& .MuiListItemText-primary': {
                                color: theme.palette.primary.contrastText,
                              },
                            },
                            '&:hover': {
                              bgcolor: theme.palette.action.hover,
                            },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <AppsIcon sx={{ fontSize: 16 }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={template.name}
                            primaryTypographyProps={{
                              variant: 'body2',
                              sx: {
                                fontWeight: (
                                  selectedComponent?.templateKey === template.id ||
                                  (template.id === 'VIEW_ALL_WEB_WIDGETS' && selectedComponent?.templateKey === 'DASHBOARD')
                                ) ? 600 : 400,
                                fontSize: '0.75rem',
                                textAlign: 'left',
                              },
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    </Tooltip>
                  ))}
                </List>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {channel.templates.map((template) => (
                    <Accordion
                      key={template.id}
                      expanded={expandedTemplates.includes(template.id)}
                      onChange={() => handleTemplateToggle(template.id)}
                      disableGutters
                      elevation={0}
                      sx={{
                        '&:before': { display: 'none' },
                        '&.Mui-expanded': { margin: 0 },
                        bgcolor: theme.palette.mode === 'dark' 
                          ? theme.palette.background.paper 
                          : theme.palette.grey[50],
                        borderRadius: 1,
                        border: theme.palette.mode === 'dark' ? '1px solid' : 'none',
                        borderColor: theme.palette.mode === 'dark' ? theme.palette.divider : 'transparent',
                      }}
                    >
                      <AccordionSummary
                        expandIcon={
                          <ExpandMoreIcon 
                            sx={{ 
                              fontSize: 16,
                              color: theme.palette.mode === 'dark' ? theme.palette.common.white : 'inherit',
                            }} 
                          />
                        }
                        sx={{
                          minHeight: 36,
                          '&.Mui-expanded': { minHeight: 36 },
                          px: 1.5,
                          py: 0.5,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {template.icon && (
                            <Chip
                              label={template.icon}
                              size="small"
                              variant="outlined"
                              sx={{
                                height: 20,
                                minWidth: 60,
                                fontSize: '0.65rem',
                                borderColor: theme.palette.divider,
                                '& .MuiChip-label': {
                                  px: 1,
                                  textAlign: 'center',
                                }
                              }}
                            />
                          )}
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              color: theme.palette.text.primary,
                              fontSize: '0.875rem',
                              textAlign: 'left',
                            }}
                          >
                            {template.name}
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pl: 2, py: 0 }}>
                        <List dense disablePadding>
                          {template.components.map((component) => (
                            <Tooltip key={component.id} title={component.id} placement="right">
                              <ListItem
                                disablePadding
                                sx={{ mb: 0.5 }}
                              >
                                <ListItemButton
                                  onClick={() =>
                                    handleComponentClick(
                                      template.productType ?? template.id,
                                      component.id,
                                      template.fuelType
                                    )
                                  }
                                  selected={component.selected}
                                  sx={{
                                    borderRadius: 1,
                                    py: 0.5,
                                    px: 1.5,
                                    minHeight: 32,
                                    '&.Mui-selected': {
                                      bgcolor: theme.palette.mode === 'dark' ? '#008080' : theme.palette.primary.main,
                                      color: theme.palette.primary.contrastText,
                                      '&:hover': {
                                        bgcolor: theme.palette.mode === 'dark' ? '#008080' : theme.palette.primary.main,
                                      },
                                      '& .MuiListItemIcon-root': {
                                        color: theme.palette.primary.contrastText,
                                      },
                                      '& .MuiListItemText-primary': {
                                        color: theme.palette.primary.contrastText,
                                      },
                                    },
                                    '&:hover': {
                                      bgcolor: theme.palette.action.hover,
                                    },
                                  }}
                                >
                                  <ListItemIcon sx={{ minWidth: 32 }}>
                                    <AppsIcon sx={{ fontSize: 16 }} />
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={component.name}
                                    primaryTypographyProps={{
                                      variant: 'body2',
                                      sx: {
                                        fontWeight: component.selected ? 600 : 400,
                                        fontSize: '0.75rem',
                                        textAlign: 'left',
                                      },
                                    }}
                                  />
                                </ListItemButton>
                              </ListItem>
                            </Tooltip>
                          ))}
                        </List>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        );
      })}

      {channels.length === 0 && (
        <Box sx={{ p: 3 }}>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, textAlign: 'left' }}>
            No templates found
          </Typography>
        </Box>
      )}
    </Box>
  );

  // Default locale options if none provided 
  const localeOptions = availableLocales.length > 0 
    ? availableLocales 
    : ['en_US'];

  return (
    <Box sx={{ 
      width: isOpen ? 320 : 48, 
      minWidth: isOpen ? 320 : 48,
      display: 'flex', 
      flexDirection: 'column', 
      gap: 2,
      transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      overflow: 'hidden',
      position: 'relative',
      height: '100%',
    }}>
      {/* Collapse/Expand Icon */}
      <Tooltip 
        title={isOpen ? 'Collapse Templates bar' : 'Expand Templates bar'} 
        placement={isOpen ? 'top' : 'right'}
      >
        <IconButton
          size="small"
          onClick={onToggleOpen}
          sx={{
            position: 'absolute',
            top: 12,
            right: isOpen ? 12 : 8,
            zIndex: 10,
            bgcolor: theme.palette.mode === 'dark' 
              ? theme.palette.background.paper 
              : theme.palette.grey[100],
            border: '1px solid',
            borderColor: theme.palette.divider,
            color: theme.palette.mode === 'dark' ? theme.palette.common.white : 'inherit',
            '&:hover': {
              bgcolor: theme.palette.mode === 'dark' 
                ? theme.palette.action.hover 
                : theme.palette.grey[200],
            },
          }}
        >
          {isOpen ? <PanelLeftClose size={16} /> : <PanelRightOpen size={16} />}
        </IconButton>
      </Tooltip>

      {/* Sidebar Card */}
      <Card sx={{ 
        p: isOpen ? 3 : 1, 
        borderRadius: 2, 
        boxShadow: 1, 
        position: 'relative', 
        width: 320,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}>
        {isOpen && (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'auto' }}>
            <Box sx={{ flexShrink: 0, pb: 2 }}>
              {/* Color Details Section */}
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: theme.palette.text.primary, textAlign: 'left' }}>
                Color Details
              </Typography>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: theme.palette.text.secondary, textAlign: 'left' }}>
                  Color Value (Hex)
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {/* Hidden color picker input */}
                  {!isReadOnly && (
                    <input
                      ref={colorPickerRef}
                      type="color"
                      value={hexValue}
                      onChange={handleColorPickerChange}
                      style={{
                        position: 'absolute',
                        opacity: 0,
                        width: 0,
                        height: 0,
                        pointerEvents: 'none',
                      }}
                    />
                  )}
                  <Tooltip title={isReadOnly ? hexValue : "Click to open color picker"} placement="top">
                    <Box
                      onClick={handleColorSwatchClick}
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1,
                        bgcolor: hexValue,
                        border: '1px solid',
                        borderColor: theme.palette.divider,
                        flexShrink: 0,
                        cursor: isReadOnly ? 'default' : 'pointer',
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        '&:hover': isReadOnly ? {} : {
                          transform: 'scale(1.05)',
                          boxShadow: 2,
                        },
                      }}
                    />
                  </Tooltip>
                  <TextField
                    value={hexValue}
                    onChange={onHexChange}
                    onBlur={onHexBlur}
                    placeholder="#FFFFFF"
                    size="small"
                    fullWidth
                    disabled={isReadOnly}
                    InputProps={{
                      readOnly: isReadOnly,
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        bgcolor: theme.palette.mode === 'dark' 
                          ? theme.palette.background.paper 
                          : theme.palette.grey[50],
                      },
                    }}
                  />
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Used In Section */}
              <Box sx={{ flexShrink: 0 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, color: theme.palette.text.secondary, textAlign: 'left' }}>
                  Used In
                </Typography>

                {/* Level 1 Error State */}
                {level1Error && (
                  <Box sx={{
                    p: 1.5,
                    mb: 2,
                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(211, 47, 47, 0.1)' : 'rgba(211, 47, 47, 0.05)',
                    border: '1px solid',
                    borderColor: theme.palette.error.main,
                    borderRadius: 1,
                  }}>
                    <Typography variant="caption" sx={{ color: theme.palette.error.main, display: 'block', fontWeight: 600, mb: 0.5 }}>
                      Configuration Error
                    </Typography>
                    <Typography variant="caption" sx={{ color: theme.palette.error.main, fontSize: '0.7rem' }}>
                      {level1Error}
                    </Typography>
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block', mt: 1, fontSize: '0.65rem' }}>
                      Template fuel types may not display correctly. Please refresh the page.
                    </Typography>
                  </Box>
                )}

                {/* Level 1 Loading State */}
                {level1Loading && (
                  <Box sx={{
                    p: 1.5,
                    mb: 2,
                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.02)',
                    border: '1px dashed',
                    borderColor: theme.palette.divider,
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                  }}>
                    <CircularProgress size={16} />
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.75rem' }}>
                      Loading template configuration...
                    </Typography>
                  </Box>
                )}

                {loading ? (
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, textAlign: 'left' }}>
                    Loading...
                  </Typography>
                ) : channels.length > 0 ? (
                  <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                    {renderTemplateUsage()}
                  </Box>
                ) : (
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, textAlign: 'left' }}>
                    No templates found
                  </Typography>
                )}
              </Box>

              <Box component="hr" sx={{ my: 3, border: 'none', borderTop: '1px solid', borderColor: theme.palette.divider }} />

              {/* Template Variants Section */}
              <Box sx={{ flexShrink: 0 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: theme.palette.text.primary, textAlign: 'left' }}>
                  Template Variants
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Hierarchy Dropdown */}
                  <FormControl size="small" fullWidth>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        mb: 0.5, 
                        color: theme.palette.text.secondary, 
                        textAlign: 'left',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                      }}
                    >
                      <AccountTreeOutlinedIcon sx={{ fontSize: 18 }} />
                      Hierarchy
                    </Typography>
                    <Select
                      value={variant.hierarchy}
                      onChange={(e) => {
                        const v = e.target.value as string;
                        if (v === 'Project') {
                          onVariantChange({ hierarchy: 'Project', clusterId: undefined });
                        } else if (v.startsWith('Cluster:')) {
                          const ref = v.replace(/^Cluster:/, '');
                          const c = availableClusters.find((x) => x.reference === ref);
                          onVariantChange({ hierarchy: v, clusterId: c?.id });
                        } else {
                          onVariantChange({ hierarchy: v });
                        }
                      }}
                      onOpen={() => onClustersDropdownOpen?.()}
                      IconComponent={ExpandMoreIcon}
                      sx={{
                        bgcolor: theme.palette.mode === 'dark' 
                          ? theme.palette.background.paper 
                          : theme.palette.grey[50],
                        '& .MuiSelect-icon': {
                          color: theme.palette.mode === 'dark' ? theme.palette.common.white : 'inherit',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderWidth: '1px',
                          borderStyle: 'solid',
                          borderColor: theme.palette.mode === 'dark' ? theme.palette.divider : theme.palette.grey[300],
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderWidth: '1px',
                          borderColor: theme.palette.mode === 'dark' ? '#008080' : theme.palette.primary.main,
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderWidth: '1px',
                          borderColor: theme.palette.mode === 'dark' ? '#008080' : theme.palette.primary.main,
                        },
                      }}
                    >
                      <MenuItem value="Project">
                        <Typography sx={{ textAlign: 'left', fontSize: '0.8rem' }}>Project</Typography>
                      </MenuItem>
                      {clustersLoading ? (
                        <MenuItem disabled>
                          <Typography sx={{ textAlign: 'left', fontSize: '0.8rem' }}>Clusters…</Typography>
                        </MenuItem>
                      ) : availableClusters.length > 0 ? (
                        availableClusters.map((c) => (
                          <Tooltip key={c.reference} title={c.name} placement="right">
                            <MenuItem value={`Cluster:${c.reference}`}>
                              <Typography sx={{ textAlign: 'left', fontSize: '0.8rem' }}>{c.reference}</Typography>
                            </MenuItem>
                          </Tooltip>
                        ))
                      ) : projectId ? (
                        <MenuItem disabled value="" sx={{ opacity: 0.8 }}>
                          <Typography sx={{ textAlign: 'left', fontSize: '0.8rem' }}>
                            No content cluster is present.
                          </Typography>
                        </MenuItem>
                      ) : null}
                    </Select>
                  </FormControl>

                  {/* Locale Dropdown */}
                  <FormControl size="small" fullWidth>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        mb: 0.5, 
                        color: theme.palette.text.secondary, 
                        textAlign: 'left',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                      }}
                    >
                      <TranslateOutlinedIcon sx={{ fontSize: 18 }} />
                      Locale
                    </Typography>
                    <Select
                      value={variant.locale}
                      onChange={(e) => onVariantChange({ locale: e.target.value })}
                      IconComponent={ExpandMoreIcon}
                      sx={{
                        bgcolor: theme.palette.mode === 'dark' 
                          ? theme.palette.background.paper 
                          : theme.palette.grey[50],
                        '& .MuiSelect-select': {
                          textAlign: 'left',
                        },
                        '& .MuiSelect-icon': {
                          color: theme.palette.mode === 'dark' ? theme.palette.common.white : 'inherit',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderWidth: '1px',
                          borderStyle: 'solid',
                          borderColor: theme.palette.mode === 'dark' ? theme.palette.divider : theme.palette.grey[300],
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderWidth: '1px',
                          borderColor: theme.palette.mode === 'dark' ? '#008080' : theme.palette.primary.main,
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderWidth: '1px',
                          borderColor: theme.palette.mode === 'dark' ? '#008080' : theme.palette.primary.main,
                        },
                      }}
                    >
                      {localeOptions.map((locale) => (
                        <MenuItem key={locale} value={locale} sx={{ fontSize: '0.8rem', textAlign: 'left' }}>
                          {locale}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Data Scenario Dropdown */}
                  <FormControl size="small" fullWidth>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        mb: 0.5, 
                        color: theme.palette.text.secondary, 
                        textAlign: 'left',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                      }}
                    >
                      <DataObjectOutlinedIcon sx={{ fontSize: 18 }} />
                      Data Scenario
                      {scenariosLoading && (
                        <CircularProgress size={12} sx={{ ml: 1 }} />
                      )}
                    </Typography>
                    <Select
                      value={variant.scenario}
                      onChange={(e) => onVariantChange({ scenario: e.target.value })}
                      displayEmpty
                      disabled={scenariosLoading}
                      IconComponent={ExpandMoreIcon}
                      sx={{
                        bgcolor: theme.palette.mode === 'dark' 
                          ? theme.palette.background.paper 
                          : theme.palette.grey[50],
                        '& .MuiSelect-icon': {
                          color: theme.palette.mode === 'dark' ? theme.palette.common.white : 'inherit',
                        },
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderWidth: '1px',
                          borderStyle: 'solid',
                          borderColor: theme.palette.mode === 'dark' ? theme.palette.divider : theme.palette.grey[300],
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderWidth: '1px',
                          borderColor: theme.palette.mode === 'dark' ? '#008080' : theme.palette.primary.main,
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderWidth: '1px',
                          borderColor: theme.palette.mode === 'dark' ? '#008080' : theme.palette.primary.main,
                        },
                      }}
                    >
                      {availableScenarios.map((scenario) => (
                        <MenuItem key={scenario.value} value={scenario.value}>
                          <Typography sx={{ textAlign: 'left', fontSize: '0.8rem' }}>{scenario.label}</Typography>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </Card>
    </Box>
  );
};

export default EditColorSidebar;
